// Single multiplexed chat WebSocket (mirrors iOS ChatStore). Protocol
// (backend app/api/v1/chat.py):
//   connect: GET /api/v1/chat/ws?token=<access>  (token in query — WS can't set headers)
//   client→server: {type:"subscribe"|"unsubscribe", room_id} · {type:"pong"}
//   server→client: {type:"ping"} · {type:"message", message:<MessageRead>} · acks · {type:"error"}
import { API_BASE_URL, API_PREFIX } from "./client";
import { tokenStore } from "./token-store";
import type { MessageRead } from "./community-types";

type MessageListener = (msg: MessageRead) => void;
type StatusListener = (connected: boolean) => void;

function wsUrl(token: string): string {
  const base = API_BASE_URL.replace(/^http/, "ws");
  return `${base}${API_PREFIX}/chat/ws?token=${encodeURIComponent(token)}`;
}

type FrameListener = (frame: Record<string, unknown>) => void;

class ChatSocket {
  private ws: WebSocket | null = null;
  private subscribed = new Set<string>();
  private messageListeners = new Set<MessageListener>();
  private frameListeners = new Set<FrameListener>();
  private statusListeners = new Set<StatusListener>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closedByUser = false;

  connect(): void {
    this.closedByUser = false;
    const token = tokenStore.access;
    if (!token) return;
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    const ws = new WebSocket(wsUrl(token));
    this.ws = ws;
    ws.onopen = () => {
      this.emitStatus(true);
      for (const room of this.subscribed) this.send({ type: "subscribe", room_id: room });
    };
    ws.onmessage = (ev: MessageEvent) => this.handleFrame(ev.data);
    ws.onclose = () => {
      this.emitStatus(false);
      if (!this.closedByUser) this.scheduleReconnect();
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    };
  }

  disconnect(): void {
    this.closedByUser = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  subscribe(roomId: string): void {
    this.subscribed.add(roomId);
    this.send({ type: "subscribe", room_id: roomId });
  }

  unsubscribe(roomId: string): void {
    this.subscribed.delete(roomId);
    this.send({ type: "unsubscribe", room_id: roomId });
  }

  onMessage(cb: MessageListener): () => void {
    this.messageListeners.add(cb);
    return () => this.messageListeners.delete(cb);
  }

  onStatus(cb: StatusListener): () => void {
    this.statusListeners.add(cb);
    return () => this.statusListeners.delete(cb);
  }

  /** Generic frame listener — receives every non-ping frame (chat messages AND
   *  co-op envelopes like state_full / participant_joined / question_advance). */
  onFrame(cb: FrameListener): () => void {
    this.frameListeners.add(cb);
    return () => this.frameListeners.delete(cb);
  }

  private send(obj: Record<string, unknown>): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(obj));
  }

  private handleFrame(raw: unknown): void {
    let frame: { type?: string; message?: MessageRead };
    try {
      frame = JSON.parse(String(raw));
    } catch {
      return;
    }
    if (frame?.type === "ping") {
      this.send({ type: "pong" });
      return;
    }
    for (const l of this.frameListeners) l(frame as Record<string, unknown>);
    if (frame?.type === "message" && frame.message) {
      for (const l of this.messageListeners) l(frame.message);
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, 3000);
  }

  private emitStatus(connected: boolean): void {
    for (const l of this.statusListeners) l(connected);
  }
}

export const chatSocket = new ChatSocket();
